import React, { useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { AiFillStar, AiOutlineStar } from 'react-icons/ai';
import { MdArrowBack, MdInfo } from 'react-icons/md';
import { useAuth } from '../AuthContext';
import { api } from '../api';
import logger from '../utils/logger';
import ProductUnavailableModal from './ProductUnavailableModal';

const MOBILE_BREAKPOINT = 700;

const ProductDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [product, setProduct] = useState(location.state?.product || null);
  const [loading, setLoading] = useState(!product);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [unavailableProduct, setUnavailableProduct] = useState(null);
  const [showUnavailableModal, setShowUnavailableModal] = useState(false);

  // Handle add to cart logic - mirrors ProductCard functionality
  const handleAddToCart = async () => {
    // ✅ Check if user is logged in first
    if (!user || !user.userId) {
      logger.warn('⚠️ User not logged in - redirecting to login page');
      alert('Please login to add products to cart');
      navigate('/');
      return;
    }

    try {
      setIsCheckingAvailability(true);
      logger.log(`🛒 Checking availability for product ${product.id}...`);

      const response = await api.post('/aastrog_cart/products/check-availability', {
        productId: product.id,
        quantity: 1
      });

      if (response.data.success && response.data.available) {
        // Product is available - add to cart in database
        logger.log(`✅ Product ${product.id} is available - adding to cart`);

        const userId = user.userId;
        console.log('📦 Adding to cart with userId:', userId);

        // Call API to add item to cart
        const addResponse = await api.post('/aastrog_cart/cart/add', {
          userId: Number(userId),
          productId: product.id,
          quantity: 1
        });

        if (addResponse.data.success) {
          logger.log(`✅ Product added to cart successfully`);
          console.log('✅ Add to cart response:', addResponse.data);

          // Navigate to cart page immediately
          console.log('🛒 Navigating to cart...');
          navigate('/cart');
        } else {
          logger.error('❌ Failed to add product to cart');
          console.error('Add to cart error:', addResponse.data);
          alert('❌ Failed to add product to cart. Please try again.');
        }
      } else {
        // Product is NOT available - show modal
        logger.warn(`❌ Product ${product.id} is not available`);
        setUnavailableProduct({
          productName: product.name,
          category: product.category,
          price: product.price,
          stockQty: product.stockQty
        });
        setShowUnavailableModal(true);
      }
    } catch (error) {
      logger.error('❌ Error adding to cart:', error);
      console.error('Cart error details:', error.response?.data || error.message);
      alert('❌ Error adding to cart. Please try again.');
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  useEffect(() => {
    // If product already provided in navigation state, skip fetch
    if (product) return;

    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError(null);
        logger.log(`Fetching product details for id=${id}`);
        // Backend provides GET /products/:id
        const resp = await api.get(`/aastrog_cart/products/${id}`);

        // Adapt depending on API shape
        const data = resp.data?.data || resp.data;
        if (resp.data && resp.data.success && Array.isArray(data) && data.length > 0) {
          const p = data[0];
          const transformed = {
            id: p.ProductId,
            name: p.ProductName,
            category: p.CategoryName || 'uncategorized',
            price: p.Price,
            rating: 4.5 + Math.random() * 0.4,
            reviews: Math.floor(Math.random() * 500) + 50,
            image: p.ImageUrl || 'https://via.placeholder.com/280x200?text=Product',
            badge: p.IsFeatured ? 'Featured' : null,
            desc: p.ProductDescription,
            sku: p.SKU,
            stockQty: p.StockQty,
            discountValue: p.DiscountValue || 0,
            discountName: p.DiscountName || null,
          };
          setProduct(transformed);
        } else if (resp.data && resp.data.success && resp.data.data && typeof resp.data.data === 'object') {
          const p = resp.data.data;
          setProduct({
            id: p.ProductId || id,
            name: p.ProductName || p.name || 'Product',
            category: p.CategoryName || 'uncategorized',
            price: p.Price || 0,
            rating: p.rating || 4.5,
            reviews: p.reviews || 0,
            image: p.ImageUrl || 'https://via.placeholder.com/280x200?text=Product',
            badge: p.IsFeatured ? 'Featured' : null,
            desc: p.ProductDescription || p.desc,
            sku: p.SKU,
            stockQty: p.StockQty,
            discountValue: p.DiscountValue || 0,
          });
        } else {
          setError('Product not found');
        }
      } catch (err) {
        logger.error('Failed to fetch product details', err);
        setError(err.message || 'Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id, product]);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="large-container">
      <div className="product-details-page">
        {/* Header - same structure as Cart.js */}
        <div className="cart-header product-details-header-inline">
          <button className="back-button product-details-back-button" onClick={() => navigate(-1)}>
            <MdArrowBack size={isMobile ? 16 : 24} />
            {!isMobile && 'Back'}
          </button>
          <h1 className="cart-title">
            <MdInfo size={isMobile ? 24 : 32} /> Product Details
          </h1>
        </div>

        {loading ? (
          <div className="products-loading">
            <div className="loading-spinner"></div>
            <p>Loading product details...</p>
          </div>
        ) : error ? (
          <div className="products-error">
            <h3>Error</h3>
            <p>{error}</p>
          </div>
        ) : product ? (
          <div className={`product-details-container ${isMobile ? 'mobile' : 'desktop'}`}>
            <div className="product-details-image">
              <img src={product.image} alt={product.name} />
              {product.badge && <span className="product-badge">{product.badge}</span>}
            </div>
            <div className="product-details-info">
              <h3 className="product-name">{product.name}</h3>
              
              <div className="product-rating">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="star-icon">
                    {i < Math.floor(product.rating) ? (
                      <AiFillStar size={16} color="#ffd700" />
                    ) : (
                      <AiOutlineStar size={16} color="#d4af37" />
                    )}
                  </span>
                ))}
                <span className="rating-number">{Number(product.rating).toFixed(1)}</span>
                <span className="review-count">({product.reviews} reviews)</span>
              </div>

              <div className="product-meta-quick">
                <p className="product-category">Category: <strong>{product.category}</strong></p>
                <p className="product-price">Price: <strong className="price-value">₹{product.price}</strong></p>
                {product.discountValue ? <p className="product-discount">Discount: <strong>{product.discountValue}</strong></p> : null}
              </div>

              <div className="product-description">
                <h4>Description</h4>
                <p>{product.desc || 'No description available.'}</p>
              </div>

              <div className="product-meta">
                <div className="meta-row">
                  <span className="meta-label">SKU:</span>
                  <span className="meta-value">{product.sku || '—'}</span>
                </div>
              </div>

              {/* Review and Rating Section */}
              <div className="product-reviews-section">
                <h4>Customer Reviews & Ratings</h4>
                <div className="reviews-container">
                  <div className="overall-rating">
                    <div className="rating-display">
                      <span className="big-rating">{Number(product.rating).toFixed(1)}</span>
                      <span className="max-rating">/5</span>
                    </div>
                    <div className="rating-stars">
                      {[...Array(5)].map((_, i) => (
                        <span key={i} className="star-icon-lg">
                          {i < Math.floor(product.rating) ? (
                            <AiFillStar size={20} color="#ffd700" />
                          ) : (
                            <AiOutlineStar size={20} color="#d4af37" />
                          )}
                        </span>
                      ))}
                    </div>
                    <span className="review-count-lg">({product.reviews} reviews)</span>
                  </div>
                  <div className="sample-reviews">
                    <p className="reviews-note">✓ Verified Reviews from Customers</p>
                    <div className="review-item">
                      <div className="reviewer-info">
                        <span className="reviewer-name">Customer 1</span>
                        <div className="reviewer-stars">
                          {[...Array(5)].map((_, i) => (
                            <span key={i} className="tiny-star">★</span>
                          ))}
                        </div>
                      </div>
                      <p className="review-text">"Excellent service! Highly recommended." - Verified Purchase</p>
                    </div>
                    <div className="review-item">
                      <div className="reviewer-info">
                        <span className="reviewer-name">Customer 2</span>
                        <div className="reviewer-stars">
                          {[...Array(4)].map((_, i) => (
                            <span key={i} className="tiny-star">★</span>
                          ))}
                        </div>
                      </div>
                      <p className="review-text">"Very satisfied with the quality and accuracy." - Verified Purchase</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="product-actions">
                {/* Unified add-to-cart button — single element for desktop & mobile */}
                <button
                  className="btn-add-cart btn-add-cart-mobile unified-add-cart"
                  onClick={handleAddToCart}
                  disabled={isCheckingAvailability}
                  aria-label="Add to cart"
                >
                  <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 24 24" height="16" width="16" xmlns="http://www.w3.org/2000/svg" style={{marginRight: '8px'}}>
                    <path fill="none" d="M0 0h24v24H0zm18.31 6-2.76 5z"></path>
                    <path d="M11 9h2V6h3V4h-3V1h-2v3H8v2h3v3zm-4 9c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zm10 0c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2zm-9.83-3.25.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.86-7.01L19.42 4h-.01l-1.1 2-2.76 5H8.53l-.13-.27L6.16 6l-.95-2-.94-2H1v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.13 0-.25-.11-.25-.25z"></path>
                  </svg>
                  {isCheckingAvailability ? 'Checking...' : 'Add to Cart'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="no-product-found">
            <h3>Product {id} details not available</h3>
            <p>If you navigated here directly, try going back and opening the product from the list.</p>
          </div>
        )}
      </div>

      {/* Product Unavailable Modal */}
      {showUnavailableModal && unavailableProduct && (
        <ProductUnavailableModal
          product={unavailableProduct}
          onClose={() => setShowUnavailableModal(false)}
        />
      )}
    </div>
  );
};

export default ProductDetails;
